import { useRouter } from 'next/router'
import { rem } from 'polished'
import React, { useEffect, useState } from 'react'
import styled from 'styled-components'
import Icon, { ICON_SIZE } from 'components/Icon'
import { Flex, Box } from 'components/Layout'
import { Text } from 'components/Typography'
import { BORDER_WIDTH, COLOR, FONT_SIZE, FONT_WEIGHT, SPACE } from 'Theme'
import { useMedia } from 'useMedia'
import { type NavLink, Wrapper } from './NavLink'

const MOBILE_OPTION_HEIGHT = 54

const StyledWrapper = styled(Wrapper)`
  justify-content: space-between;
  ${({ isMobile }) =>
    isMobile &&
    `
      border-bottom: ${BORDER_WIDTH.PX_1} solid ${COLOR.GRAYSCALE_5};
    `}
`

const IconWrapper = styled('div')`
  ${({ isOpened }) => isOpened && `transform: rotate(180deg)`};
  transition: 500ms;
`

const ChildrenWrapper = styled('div')`
  transition: 500ms;
  transform-origin: top;
  ${({ isOpened, optionsLength }) => {
    if (isOpened) {
      return `
        max-height: ${MOBILE_OPTION_HEIGHT * optionsLength}px;
        opacity: 1;
        transform: scaleY(1);
      `
    }
    return `
      max-height: 0;
      transform: scaleY(0);
      opacity: 0;
    `
  }}
`
interface NavLinkWrapperProps {
  label?: string
  icon?: React.ElementType
}

export const NavLinkWrapper: React.FC<React.PWC<NavLinkWrapperProps>> = ({
  children,
  label,
  icon: LinkIcon,
}) => {
  const [isActive, setIsActive] = useState(false)
  const [isOpened, setIsOpened] = useState(false)
  const router = useRouter()
  const media = useMedia()
  const isMobile = media.MOBILE
  const optionsLength = React.Children.count(children)

  useEffect(() => {
    const isCurrentRoute = React.Children.toArray(children).some(
      (element) =>
        React.isValidElement(element) && element.props.href === router.pathname
    )
    setIsActive(isCurrentRoute)
    setIsOpened((state) => isCurrentRoute || state)
  }, [children, router.pathname])

  return (
    <Box>
      <StyledWrapper
        height={{ MOBILE: rem(MOBILE_OPTION_HEIGHT), TABLET: rem(40) }}
        pl={isMobile ? SPACE.PX_30 : SPACE.PX_15}
        alignItems="center"
        isActive={isActive}
        isMobile={isMobile}
        onClick={() => setIsOpened((state) => !state)}
      >
        <Flex alignItems="center">
          {LinkIcon && !isMobile && (
            <Flex
              width={SPACE.PX_20}
              height={SPACE.PX_20}
              mr={SPACE.PX_10}
              alignItems="center"
              justifyContent="center"
            >
              <LinkIcon
                color={isActive ? COLOR.PRIMARY_1 : COLOR.GRAYSCALE_2}
                size={ICON_SIZE.PX_18}
                aria-label={label}
              />
            </Flex>
          )}
          {label && (
            <Text
              fontWeight={isActive ? FONT_WEIGHT.MEDIUM : FONT_WEIGHT.NORMAL}
              color={isActive ? COLOR.GRAYSCALE_1 : COLOR.GRAYSCALE_2}
              fontSize={isMobile ? FONT_SIZE.PX_16 : FONT_SIZE.PX_14}
            >
              {label}
            </Text>
          )}
        </Flex>
        {isMobile && (
          <IconWrapper isOpened={isOpened}>
            <Icon.ArrowDown color={COLOR.GRAYSCALE_3} />
          </IconWrapper>
        )}
      </StyledWrapper>
      <ChildrenWrapper optionsLength={optionsLength} isOpened={isOpened}>
        {React.Children.map(children, (child) => {
          if (!React.isValidElement(child)) {
            return null
          }

          return React.cloneElement<
            Partial<React.ComponentPropsWithoutRef<typeof NavLink>>
          >(child, {
            isSubOption: true,
          })
        })}
      </ChildrenWrapper>
    </Box>
  )
}
